// PhotoTile.jsx

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './PhotoTile.css';
import 'react-loading-skeleton/dist/skeleton.css';

// Components
import Skeleton from 'react-loading-skeleton';

/**
 * PhotoTile
 *
 * Renders a photo inside a NodeGrid cell, ensuring it fills the space
 * and crops center using object-fit: cover.
 *
 * @param {object} node - The photo node to display
 * 
 * @returns {ReactNode}
 */
const PhotoTile = ({ node, onClick }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [imageUrl, setImageUrl] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // ----- MEMOS ----- //

  const candidateUrls = useMemo(() => (
    [node?.url_grid, node?.url_thumb, node?.url]
      .filter((value, index, arr) => typeof value === 'string' && value.trim() && arr.indexOf(value) === index)
  ), [node?.url_grid, node?.url_thumb, node?.url]);

  // ----- EFFECTS ----- //

  useLayoutEffect(() => {
    setIsLoaded(false);
    setImageUrl(null);
  }, [node?.key, candidateUrls]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nodeKey = node?.key || null;

      for (const rawUrl of candidateUrls) {
        const url = await resolveImageUrl({ nodeKey, url: rawUrl, storagePath: null });
        if (cancelled) return;
        if (url) {
          setImageUrl(url);
          return;
        }
      }

      if (node?.storagePath) {
        const fallback = await resolveImageUrl({ nodeKey, url: null, storagePath: node.storagePath });
        if (cancelled) return;
        setImageUrl(fallback || null);
        return;
      }

      setImageUrl(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [node?.key, node?.storagePath, candidateUrls]);

  // ----- RENDER ----- //

  return (
    <div
      className="photo-tile-container"
      title={node.title}
      onClick={() => onClick(node)}
    >
      {!isLoaded && (
        <Skeleton
          width="100%"
          height="100px"
          baseColor={theme?.background || undefined}
          highlightColor={theme?.backgroundMuted || undefined}
        />
      )}

      {/* IMAGE */}
      {imageUrl && (
        <img
          className="photo-tile-image"
          style={{
            opacity: isLoaded ? 1 : 0,
          }}
          alt={node.title || 'Photo'}
          src={imageUrl}
          onError={() => setIsLoaded(true)}
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
};

// Dev Tools
PhotoTile.displayName = 'PhotoTile';

export default PhotoTile;
